import React, { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { fmtUsd } from "./FinanceLayout";
import { Loader2, Wallet, TrendingUp, PiggyBank, AlertTriangle, CheckCircle2, DollarSign } from "lucide-react";
import { toast } from "sonner";

/**
 * SmartAllocationCard (v2 — Active Cash Management)
 * -------------------------------------------------
 * Divides the year's Liquid Cash (revenue − categorized expenses) into
 * three named allocation buckets: Operating, Growth, Reserve. Whatever
 * the partner does NOT allocate is what flows through to the Net
 * Distributable tile and the per-partner split.
 *
 *   Liquid Cash             = Revenue − COGS − OP Exp − Reserve Exp
 *   Sum of Allocations      = Operating + Growth + Reserve  (target dollars)
 *   Net Distributable       = Liquid Cash − Sum of Allocations
 *   Per-Partner Share       = Net Distributable / 2
 *
 * Design intent: sliders are gone — the partner types exact dollars.
 * Over-allocation is soft-blocked (red state + save disabled) so the
 * bucket totals can never claim more than what's in the tin.
 *
 * Reports `total_allocated` back up to the parent so DistributionsPage
 * can render the effective Net Distributable in the summary strip.
 */
const KEYS = ["operating", "growth", "reserve"];
const META = {
  operating: { label: "Operating", icon: Wallet,      isMaster: true  },
  growth:    { label: "Growth",    icon: TrendingUp,  isMaster: false },
  reserve:   { label: "Reserve",   icon: PiggyBank,   isMaster: false },
};

const SmartAllocationCard = ({ liquidCash = 0, onAllocationChange }) => {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState({});           // { operating: "125.50" } — string, in-flight edits
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/finance/allocation-config");
      setCfg(data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Derived numbers ─────────────────────────────────────
  const readValue = (k) => {
    if (dirty[k] !== undefined) return Number(dirty[k]) || 0;
    return Number(cfg?.[`${k}_dollars`]) || 0;
  };
  const values     = { operating: readValue("operating"), growth: readValue("growth"), reserve: readValue("reserve") };
  const totalAlloc = values.operating + values.growth + values.reserve;
  const remaining  = liquidCash - totalAlloc;
  const overspend  = remaining < 0;

  // Broadcast the live allocation total to the parent so the summary
  // strip's Net Distributable + per-partner tiles recompute in real time.
  useEffect(() => {
    onAllocationChange?.({
      operating: values.operating,
      growth:    values.growth,
      reserve:   values.reserve,
      total:     totalAlloc,
      remaining,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAlloc, remaining]);

  const setBucket = (k, v) => setDirty((prev) => ({ ...prev, [k]: v }));

  const commit = async (k) => {
    if (!user?.is_partner) return;
    if (dirty[k] === undefined) return;
    const val = Math.max(0, Number(dirty[k]) || 0);
    // Server persist — mode locked to `dollars` in the v2 UX.
    setSaving(true);
    try {
      const next = { ...cfg, mode: "dollars", [`${k}_dollars`]: val };
      await apiClient.patch("/finance/allocation-config", {
        mode:              "dollars",
        operating_pct:     cfg.operating_pct,     // required by model; unused in dollars mode
        growth_pct:        cfg.growth_pct,
        reserve_pct:       cfg.reserve_pct,
        operating_dollars: next.operating_dollars,
        growth_dollars:    next.growth_dollars,
        reserve_dollars:   next.reserve_dollars,
      });
      setCfg(next);
      setDirty((prev) => { const p = { ...prev }; delete p[k]; return p; });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
  };

  if (!cfg) {
    return (
      <div className="card-industrial p-6 mb-6">
        <Loader2 size={16} className="animate-spin text-nex-gold" />
      </div>
    );
  }

  const isPartner = !!user?.is_partner;

  return (
    <div className="card-industrial corner-tick p-5 mb-6" data-testid="smart-allocation-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-nex-gold" />
          <div className="mono-label">// ACTIVE CASH MANAGEMENT</div>
          {saving && <Loader2 size={12} className="animate-spin text-nex-muted" />}
        </div>
        {!isPartner && (
          <span className="mono-body text-[10px] uppercase tracking-widest text-nex-muted">
            Read-only · partners only
          </span>
        )}
      </div>

      {/* Liquid Cash header — the pile being divided. */}
      <div className={`p-4 mb-4 border ${overspend ? "border-red-500/50 bg-red-500/[0.04]" : "border-nex-gold/40 bg-nex-gold/[0.03]"}`} data-testid="liquid-cash-card">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="mono-body text-[10px] uppercase tracking-widest text-nex-gold">Liquid Cash Available</div>
            <div className="mono-body text-[10px] text-nex-muted mt-0.5">Revenue − COGS − OP − Reserve expenses</div>
          </div>
          <div className="serif-head text-3xl text-nex-gold">{fmtUsd(liquidCash)}</div>
        </div>
        <div className="mt-4 pt-4 border-t border-nex-border/50 grid grid-cols-3 gap-3 text-center">
          <MiniStat label="Allocated"   value={fmtUsd(totalAlloc)} tone="muted" testid="mini-allocated" />
          <MiniStat label="Remaining"   value={fmtUsd(remaining)}  tone={overspend ? "red" : remaining > 0.01 ? "emerald" : "muted"} testid="mini-remaining" />
          <MiniStat label="Utilisation" value={liquidCash > 0 ? `${Math.min(999, (totalAlloc / liquidCash) * 100).toFixed(1)} %` : "—"} tone={overspend ? "red" : "muted"} testid="mini-utilisation" />
        </div>
        {overspend && (
          <div className="mt-3 flex items-start gap-2 text-red-300 mono-body text-[11px] leading-relaxed">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>Over-allocated by <strong>{fmtUsd(Math.abs(remaining))}</strong>. Reduce a bucket before your next partner draw.</span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {KEYS.map((k) => (
          <Bucket
            key={k}
            keyName={k}
            meta={META[k]}
            value={dirty[k] !== undefined ? dirty[k] : cfg[`${k}_dollars`]}
            disabled={!isPartner}
            overspend={overspend}
            onChange={(v) => setBucket(k, v)}
            onCommit={() => commit(k)}
            onKeyDown={onKeyDown}
            liquidCash={liquidCash}
          />
        ))}
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, tone, testid }) => {
  const toneClass = tone === "red" ? "text-red-300" : tone === "emerald" ? "text-emerald-300" : "text-nex-chrome";
  return (
    <div data-testid={testid}>
      <div className="mono-body text-[9px] uppercase tracking-widest text-nex-muted">{label}</div>
      <div className={`serif-head text-lg ${toneClass}`}>{value}</div>
    </div>
  );
};

const Bucket = ({ keyName, meta, value, disabled, overspend, onChange, onCommit, onKeyDown, liquidCash }) => {
  const Icon = meta.icon;
  const numeric = Number(value) || 0;
  const pct = liquidCash > 0 ? Math.min(100, (numeric / liquidCash) * 100) : 0;
  return (
    <div className={`p-4 border transition-colors ${
      meta.isMaster
        ? overspend ? "border-red-500/50 bg-red-500/[0.03]" : "border-nex-gold/60 bg-nex-gold/[0.03]"
        : "border-nex-border bg-black/20"
    }`} data-testid={`alloc-bucket-${keyName}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={13} className={meta.isMaster ? "text-nex-gold" : "text-nex-chrome"} />
          <span className={`mono-label !text-[9px] ${meta.isMaster ? "text-nex-gold" : ""}`}>
            {meta.label.toUpperCase()}
          </span>
          {meta.isMaster && <span className="mono-body text-[8px] uppercase tracking-widest px-1.5 py-0.5 bg-nex-gold text-black">MASTER</span>}
        </div>
      </div>

      <label className="block">
        <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Amount ($)</div>
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
          onKeyDown={onKeyDown}
          disabled={disabled}
          data-testid={`alloc-${keyName}-input`}
          className="input-industrial w-full text-lg font-medium"
          placeholder="0.00"
        />
      </label>

      {/* Fill bar shows this bucket's slice of Liquid Cash */}
      <div className="mt-3 h-1.5 bg-nex-border/40 overflow-hidden">
        <div
          className={`h-full transition-all ${meta.isMaster ? "bg-nex-gold" : "bg-nex-chrome"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="mono-body text-[10px] uppercase tracking-widest text-nex-muted">
          {pct.toFixed(1)} % of liquid
        </span>
        {!disabled && (
          <span className="mono-body text-[10px] text-nex-muted flex items-center gap-1">
            <CheckCircle2 size={10} /> Autosaved on blur
          </span>
        )}
      </div>
    </div>
  );
};

export default SmartAllocationCard;
